/* eslint-disable */
import * as flatbuffers from "flatbuffers";

// ROOT
import { FrontEndMessage } from "../generated/front-end/front-end-message";
import { FrontEndMessageT } from "../generated/front-end/front-end-message";

// LN2 message chain (T types)
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { OneHotMessageContent } from "../generated/ln2/one-hot-message-content";
import { BitMaskMessageContent } from "../generated/ln2/bit-mask-message-content";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";

// TRAFFIC
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";

// CONTROL
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { ControlMessageT } from "../generated/control/control-message";

// USAGE
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";
import { UsageMessageT } from "../generated/usage/usage-message";

// FAULT
import { FaultContent } from "../generated/fault/fault-content";
import { FaultMessageT } from "../generated/fault/fault-message";

// SERVICE
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ServiceMessageT } from "../generated/service/service-message";

// STATUS
import { DeviceStatusContent } from "../generated/status/device-status-content";
import { ElevatorStatusContent } from "../generated/status/elevator-status-content";
import { EscalatorStatusContent } from "../generated/status/escalator-status-content";
import { StatusMessageT } from "../generated/status/status-message";
import { ElevatorStatusMessageT } from "../generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../generated/status/escalator-status-message";
import { ConnectionStatusMessageT } from "../generated/status/connection-status-message";

// ---------------------------------------------------------
// MAIN: Decode FrontEndMessage bytes -> FrontEndMessageT
// ---------------------------------------------------------
export function decodeFrontEndMessage(
  bytes: Uint8Array
): FrontEndMessageT | null {
  const bb = new flatbuffers.ByteBuffer(bytes);
  const fem = FrontEndMessage.getRootAsFrontEndMessage(bb).unpack();
  if (!fem.bankId || !fem.liftId || !fem.msg?.msg) return null;
  return fem;
}

// ---------------------------------------------------------
// Extract ConnectionStatusMessageT from a StatusMessageT
// ---------------------------------------------------------
export function extractConnectionStatus(
  statusMsg: StatusMessageT
): ConnectionStatusMessageT | null {
  switch (statusMsg.statusType) {
    case DeviceStatusContent.ElevatorStatusMessage: {
      const elev = statusMsg.status as ElevatorStatusMessageT | null;
      if (
        elev &&
        elev.statusType === ElevatorStatusContent.ConnectionStatusMessage
      ) {
        return elev.status as ConnectionStatusMessageT | null;
      }
      break;
    }
    case DeviceStatusContent.EscalatorStatusMessage: {
      const esc = statusMsg.status as EscalatorStatusMessageT | null;
      if (
        esc &&
        esc.statusType === EscalatorStatusContent.ConnectionStatusMessage
      ) {
        return esc.status as ConnectionStatusMessageT | null;
      }
      break;
    }
    default:
      break;
  }
  return null;
}

// ---------------------------------------------------------
// Check if a FrontEndMessageT is a fault message
// ---------------------------------------------------------
export function isFaultMessage(fem: FrontEndMessageT): boolean {
  const encoded = fem.msg?.msg;
  if (!encoded || encoded.contentType !== EncodedMessageContent.BitMaskMessage)
    return false;
  return (
    (encoded.content as BitMaskMessageT).messageType ===
    BitMaskMessageContent.Fault_FaultMessage
  );
}

// ---------------------------------------------------------
// Compute debounce key from FlatBuffer union hierarchy.
// Returns "ELEVATOR:{enumId}" / "ESCALATOR:{enumId}" /
// "STATUS:{enumId}", or undefined for unrecognised messages.
// ---------------------------------------------------------
export function computeDebounceKey(fem: FrontEndMessageT): string | undefined {
  const encoded = fem.msg?.msg;
  if (!encoded) return undefined;

  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    switch (bm.messageType) {
      case BitMaskMessageContent.Traffic_TrafficMessage: {
        const traffic = bm.message as TrafficMessageT;
        switch (traffic.messageType) {
          case DeviceTrafficMessage.ElevatorTrafficMessage:
            return `ELEVATOR:${
              (traffic.message as ElevatorTrafficMessageT).message
            }`;
          case DeviceTrafficMessage.EscalatorTrafficMessage:
            return `ESCALATOR:${
              (traffic.message as EscalatorTrafficMessageT).message
            }`;
          default:
            return undefined;
        }
      }
      case BitMaskMessageContent.Control_ControlMessage: {
        const control = bm.message as ControlMessageT;
        switch (control.controlType) {
          case DeviceControlMessage.ElevatorControlMessage:
            return `ELEVATOR:${
              (control.control as ElevatorControlMessageT).message
            }`;
          case DeviceControlMessage.EscalatorControlMessage:
            return `ESCALATOR:${
              (control.control as EscalatorControlMessageT).message
            }`;
          default:
            return undefined;
        }
      }
      case BitMaskMessageContent.Fault_FaultMessage: {
        const fault = bm.message as FaultMessageT;
        const devType =
          fault.faultType === FaultContent.ElevatorFaultMessage
            ? "ELEVATOR"
            : "ESCALATOR";
        return `${devType}:${fault.fault?.message ?? 0}`;
      }
      case BitMaskMessageContent.Status_StatusMessage: {
        const conn = extractConnectionStatus(bm.message as StatusMessageT);
        if (conn) return `STATUS:${conn.message}`;
        return undefined;
      }
      default:
        return undefined;
    }
  }

  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    switch (oh.messageType) {
      case OneHotMessageContent.Usage_UsageMessage: {
        const usage = oh.message as UsageMessageT;
        switch (usage.messageType) {
          case DeviceUsageContent.ElevatorUsageMessage:
            return `ELEVATOR:${
              (usage.message as ElevatorUsageMessageT).message
            }`;
          case DeviceUsageContent.EscalatorUsageMessage:
            return `ESCALATOR:${
              (usage.message as EscalatorUsageMessageT).message
            }`;
          default:
            return undefined;
        }
      }
      case OneHotMessageContent.Service_ServiceMessage: {
        const service = oh.message as ServiceMessageT;
        const devType =
          service.messageType === DeviceServiceContent.ElevatorServiceMessage
            ? "ELEVATOR"
            : "ESCALATOR";
        return `${devType}:${service.message?.message ?? 0}`;
      }
      default:
        return undefined;
    }
  }

  return undefined;
}
